import { Box, Button, Flex, HStack, Icon, Table, Tbody, Text, Thead, Tooltip, Tr } from '@chakra-ui/react';
import { TbArrowsSplit2, TbPlus, TbArrowMerge, TbTrash } from 'react-icons/tb';
import { SettingsTd, SettingsTh } from './primitives';
import { SETTINGS_BORDER_COLOR } from './primitives/constants';

/**
 * What happens to one company in a carrier's group when the screen saves.
 *
 * 'same' rides along on the carrier being configured — one set of rules, no row
 * of its own. 'separate' is branched: its own row, starting from the carrier's.
 * 'none' is not configured at all. 'elsewhere' is already configured outside
 * this entry, and the screen leaves it alone unless the user pulls it in.
 */
export type SubCompanyMode = 'same' | 'separate' | 'none' | 'elsewhere';

export interface SubCompanyRow {
    insurerId: string;
    name: string;
    mode: SubCompanyMode;
    /**
     * For a company configured outside this entry, the carrier whose settings it
     * follows — absent when it stands on rules of its own.
     */
    followsName?: string;
}

export interface SubCompanyTableProps {
    /** The carrier these sub companies sit under, for the action wording. */
    parentName: string;
    rows: Array<SubCompanyRow>;
    onModeChange: (insurerId: string, mode: SubCompanyMode) => void;
    /**
     * What the parent is sharing, for the action tooltips: a DRP carrier shares
     * its 'settings', an APU rule shares its 'override'.
     */
    sharedNoun?: string;
    emptyMessage: string;
    /**
     * Fill the height available in the pane instead of capping at `maxH`, so the
     * table grows with its rows and only scrolls once it runs out of room.
     */
    fill?: boolean;
    maxH?: string;
}

/**
 * Only said out loud when the rules come from somewhere this screen does not
 * control. For every other state the row's own buttons already say where it
 * stands, so a label would only repeat them.
 */
const statusLabel = (row: SubCompanyRow, sharedNoun: string): string | undefined =>
    row.mode === 'elsewhere' && row.followsName
        ? `On ${row.followsName}'s ${sharedNoun}`
        : undefined;

interface SubCompanyAction {
    icon: React.ElementType;
    label: string;
    /** Spells out what the short label does, since the buttons stay compact. */
    tooltip: string;
    mode: SubCompanyMode;
    isDestructive?: boolean;
}

/**
 * What can be done to a row, given where it stands. Branching only makes sense
 * for a company this carrier covers; one already configured on its own is edited
 * from its own row, so it is only offered the merge back.
 */
const rowActions = (
    row: SubCompanyRow,
    parentName: string,
    sharedNoun: string,
): SubCompanyAction[] => {
    switch (row.mode) {
        case 'none':
            return [
                {
                    icon: TbPlus as React.ElementType,
                    label: 'Add',
                    tooltip: `Add with ${parentName}'s ${sharedNoun}`,
                    mode: 'same',
                },
                {
                    icon: TbArrowsSplit2 as React.ElementType,
                    label: 'Add separately',
                    tooltip: `Add with a separate ${sharedNoun}`,
                    mode: 'separate',
                },
            ];
        case 'same':
            return [
                {
                    icon: TbArrowsSplit2 as React.ElementType,
                    label: 'Branch',
                    tooltip: `Branch to a separate ${sharedNoun}`,
                    mode: 'separate',
                },
                {
                    icon: TbTrash as React.ElementType,
                    label: 'Remove',
                    tooltip: `Remove from ${parentName}`,
                    mode: 'none',
                    isDestructive: true,
                },
            ];
        case 'separate':
            return [
                {
                    icon: TbArrowMerge as React.ElementType,
                    label: 'Merge',
                    tooltip: `Use ${parentName}'s ${sharedNoun}`,
                    mode: 'same',
                },
                {
                    icon: TbTrash as React.ElementType,
                    label: 'Remove',
                    tooltip: 'Remove from this group',
                    mode: 'none',
                    isDestructive: true,
                },
            ];
        default:
            return [
                {
                    icon: TbArrowMerge as React.ElementType,
                    label: 'Merge',
                    tooltip: `Use ${parentName}'s ${sharedNoun}`,
                    mode: 'same',
                },
            ];
    }
};

/**
 * The companies in a carrier's group and what happens to each one. A sub
 * company on 'same' has no row of its own in the DRP table — it is covered by
 * the carrier's rules — while a branched one becomes its own row, so this table
 * is where a group is kept together or pulled apart.
 *
 * Everything is a state change on one row rather than a checkbox, because there
 * are three states and "checked" cannot say which of the two added ones it is.
 * The row's actions sit in the open rather than behind a menu: there are at most
 * two, and the column has the width for them.
 */
const SubCompanyTable = ({
    parentName,
    rows,
    onModeChange,
    emptyMessage,
    sharedNoun = 'settings',
    fill = false,
    maxH = '320px',
}: SubCompanyTableProps) => (
    <Flex
        direction="column"
        minH={0}
        maxH={fill ? '100%' : undefined}
        bg="white"
        borderWidth="1px"
        borderColor={SETTINGS_BORDER_COLOR}
        borderRadius="lg"
        overflow="hidden"
    >
        <Box
            minH={0}
            maxH={fill ? undefined : maxH}
            overflow="auto"
            sx={{
                '& thead': { zIndex: 2 },
                '& th': {
                    bg: 'gray.50',
                    borderBottomWidth: '1px',
                    borderBottomColor: 'var(--chakra-colors-gray-200)',
                },
                '& tbody tr:last-of-type td': { borderBottomWidth: 0 },
                '& th:first-of-type, & td:first-of-type': { pl: 5 },
                '& th:last-of-type, & td:last-of-type': { pr: 5 },
                '&::-webkit-scrollbar': { width: '8px', height: '8px' },
                '&::-webkit-scrollbar-track': { background: 'transparent' },
                '&::-webkit-scrollbar-thumb': { background: '#cbd5e0', borderRadius: '4px' },
                scrollbarWidth: 'thin',
                scrollbarColor: '#cbd5e0 transparent',
            }}
        >
            <Table variant="simple" size="sm" w="100%" sx={{ tableLayout: 'fixed' }}>
                <Thead position="sticky" top={0}>
                    <Tr bg="gray.50">
                        <SettingsTh>SUB COMPANY</SettingsTh>
                        <SettingsTh w="270px" textAlign="right">
                            ACTIONS
                        </SettingsTh>
                    </Tr>
                </Thead>
                <Tbody>
                    {rows.length === 0 ? (
                        <Tr>
                            <SettingsTd colSpan={2} py={8} textAlign="center">
                                <Text fontSize="sm" color="gray.500">
                                    {emptyMessage}
                                </Text>
                            </SettingsTd>
                        </Tr>
                    ) : (
                        rows.map((row) => (
                            <Tr key={row.insurerId} _hover={{ bg: 'gray.25' }}>
                                <SettingsTd color={row.mode === 'none' ? 'gray.400' : 'gray.700'}>
                                    <Text fontSize="sm" noOfLines={1}>
                                        {row.name}
                                    </Text>
                                </SettingsTd>
                                <SettingsTd>
                                    <HStack spacing={2} justify="flex-end">
                                        {statusLabel(row, sharedNoun) && (
                                            <Text fontSize="xs" color="gray.500" noOfLines={1}>
                                                {statusLabel(row, sharedNoun)}
                                            </Text>
                                        )}
                                        {rowActions(row, parentName, sharedNoun).map((action) => (
                                            <Tooltip
                                                key={action.label}
                                                label={action.tooltip}
                                                placement="top"
                                                hasArrow
                                                openDelay={300}
                                            >
                                                <Button
                                                    size="xs"
                                                    variant="outline"
                                                    flexShrink={0}
                                                    colorScheme={action.isDestructive ? 'red' : 'gray'}
                                                    leftIcon={<Icon as={action.icon} boxSize={3.5} />}
                                                    onClick={() =>
                                                        onModeChange(row.insurerId, action.mode)
                                                    }
                                                >
                                                    {action.label}
                                                </Button>
                                            </Tooltip>
                                        ))}
                                    </HStack>
                                </SettingsTd>
                            </Tr>
                        ))
                    )}
                </Tbody>
            </Table>
        </Box>
    </Flex>
);

export default SubCompanyTable;
